// src/hooks/useChat.ts
import { useState, useLayoutEffect, useCallback, useRef } from "react";
import {
  Chat,
  Message,
  MessageMetadata,
  ChartData,
  ModelInfo,
  Databases,
  Styles,
} from "../types";
import { api } from "../services/api";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { chatService } from "../services/chatService";

interface StreamChunk {
  id?: string;
  content: string;
  metadata?: MessageMetadata;
  chart_data?: ChartData;
  model_info?: ModelInfo;
  database_info?: string;
  style_info?: string;
  is_chunk: boolean;
  is_final: boolean;
  type?: string;
  bot_message_id?: string;
}

export const useChat = (initialDatabase?: string) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeChat, setActiveChat] = useState<string | null>(null);

  const [chats, setChats] = useState<Chat[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);

  const [models, setModels] = useState<any>({});
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [isLoadingModel, setIsLoadingModel] = useState<boolean>(false);

  const databases: Databases = {
    all: { name: "ข้อมูล TATIC และ DMS", database: "all", type: "normal" },
    nonregulation: { name: "ข้อมูลสถิติ (TATIC)", database: "nonregulation", type: "normal" },
    regulation: { name: "เอกสารระเบียบ / คำสั่ง (DMS)", database: "regulation", type: "normal" },
  };
  const DEFAULT_DATABASE = initialDatabase || "all";

  const [selectedDatabase, setSelectedDatabase] = useState<string>(DEFAULT_DATABASE);
  const [isLoadingDatabase, setIsLoadingDatabase] = useState<boolean>(false);

  const styles: Styles = {
    normal: { name: "ทั่วไป", style: "normal", type: "normal" },
    analysis: { name: "เชิงวิเคราะห์", style: "analysis", type: "normal" },
  };
  const DEFAULT_STYLE = "normal";

  const [selectedStyle, setSelectedStyle] = useState<string>(DEFAULT_STYLE);
  const [isLoadingStyle, setIsLoadingStyle] = useState<boolean>(false);

  const [chatPage, setChatPage] = useState(0);
  const [hasMoreChats, setHasMoreChats] = useState(true);

  const [messagePage, setMessagePage] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  const [newRoomCreated, setNewRoomCreated] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const ITEMS_PER_PAGE = 20;
  const MESSAGES_PER_PAGE = 4;

  const location = useLocation();
  const navigate = useNavigate();

  const fetchChats = async (skip: number = 0) => {
    try {
      setIsLoading(true);
      if (skip !== 0) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      const response = await chatService.fetchChats(skip, ITEMS_PER_PAGE);
      const data = await response.data;
      const fetchedChats = data.chats.map((chat: any) => ({
        ...chat,
        created: new Date(chat.created),
        updated: new Date(chat.updated),
      }));
      setChats((prevChats) =>
        skip === 0 ? fetchedChats : [...prevChats, ...fetchedChats]
      );
      setHasMoreChats(fetchedChats.length === ITEMS_PER_PAGE);
      return fetchedChats.length;
    } catch (error) {
      console.error("Error fetching chats:", error);
      return 0;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async (chatId: string, skip: number = 0) => {
    if (!chatId || chatId === "new") return;
    try {
      setIsLoadingMessages(true);
      if (skip !== 0) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      const response = await chatService.fetchMessages(
        chatId,
        skip,
        MESSAGES_PER_PAGE
      );
      const fetchedMessages = response.data.messages;
      setMessages((prevMessages) => {
        if (skip === 0) {
          return [...fetchedMessages].reverse();
        }
        return [...[...fetchedMessages].reverse(), ...prevMessages];
      });
      setHasMoreMessages(fetchedMessages.length === MESSAGES_PER_PAGE);
      return fetchedMessages.length;
    } catch (error) {
      console.error("Error fetching messages:", error);
      navigate("/");
      return 0;
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleLoadMoreChats = useCallback(async () => {
    if (isLoading || !hasMoreChats) return;
    const nextPage = chatPage + 1;
    const fetchedCount = await fetchChats(nextPage * ITEMS_PER_PAGE);
    if (fetchedCount > 0) {
      setChatPage(nextPage);
    }
  }, [chatPage, isLoading, hasMoreChats]);

  const handleLoadMoreMessages = useCallback(async () => {
    if (isLoadingMessages || !hasMoreMessages || !activeChat) return;
    const nextPage = messagePage + 1;
    const fetchedCount = await fetchMessages(
      activeChat,
      nextPage * MESSAGES_PER_PAGE
    );
    if (fetchedCount > 0) {
      setMessagePage(nextPage);
    }
  }, [messagePage, isLoadingMessages, hasMoreMessages, activeChat]);

  const handleSelectModel = useCallback(async (model: string) => {
    try {
      setIsLoadingModel(true);
      setSelectedModel(model);
      sessionStorage.setItem("selectedModel", model);
    } catch (error) {
      console.error("Error updating model:", error);
      const previousModel = sessionStorage.getItem("selectedModel");
      if (previousModel) {
        setSelectedModel(previousModel);
      }
    } finally {
      setIsLoadingModel(false);
    }
  }, []);

  const handleSelectDatabase = useCallback((database: string) => {
    try {
      setIsLoadingDatabase(true);
      if (!databases[database] || databases[database].type === "maintenance") {
        setSelectedDatabase(DEFAULT_DATABASE);
        sessionStorage.setItem("selectedDatabase", DEFAULT_DATABASE);
        return;
      }
      setSelectedDatabase(database);
      sessionStorage.setItem("selectedDatabase", database);
    } catch (error) {
      console.error("Error updating database:", error);
      setSelectedDatabase(DEFAULT_DATABASE);
      sessionStorage.setItem("selectedDatabase", DEFAULT_DATABASE);
    } finally {
      setIsLoadingDatabase(false);
    }
  }, [databases]);

  const handleSelectStyle = useCallback((style: string) => {
    try {
      setIsLoadingStyle(true);
      if (!styles[style]) {
        setSelectedStyle(DEFAULT_STYLE);
        sessionStorage.setItem("selectedStyle", DEFAULT_STYLE);
        return;
      }
      setSelectedStyle(style);
      sessionStorage.setItem("selectedStyle", style);
    } catch (error) {
      console.error("Error updating style:", error);
      setSelectedStyle(DEFAULT_STYLE);
      sessionStorage.setItem("selectedStyle", DEFAULT_STYLE);
    } finally {
      setIsLoadingStyle(false);
    }
  }, [styles]);

  useLayoutEffect(() => { fetchChats(0); }, []);

  useLayoutEffect(() => {
    const chatId = location.pathname.split("/").pop();
    if (chatId && chatId !== "new") {
      if (newRoomCreated) {
        setNewRoomCreated(false);
      } else {
        setActiveChat(chatId);
        setMessagePage(0);
        setHasMoreMessages(true);
        setMessages([]);
        fetchMessages(chatId, 0);
      }
    }
  }, [location.pathname]);

  useLayoutEffect(() => {
    const fetchModels = async () => {
      try {
        setIsLoadingModel(true);
        const response = await api.get("/models");
        const data = response.data;
        setModels(data.available_models);
        const savedModel = sessionStorage.getItem("selectedModel");
        const initialModel = savedModel || data.default_model;
        setSelectedModel(initialModel);
      } catch (error) {
        console.error("Error fetching models:", error);
      } finally {
        setIsLoadingModel(false);
      }
    };
    fetchModels();
  }, [activeChat]);

  useLayoutEffect(() => {
    if (selectedModel) sessionStorage.setItem("selectedModel", selectedModel);
  }, [selectedModel]);

  useLayoutEffect(() => {
    try {
      const savedDatabase = sessionStorage.getItem("selectedDatabase");
      let initialDatabase = DEFAULT_DATABASE;
      if (savedDatabase && databases[savedDatabase] && databases[savedDatabase].type !== "maintenance") {
        initialDatabase = savedDatabase;
      }
      setSelectedDatabase(initialDatabase);
      sessionStorage.setItem("selectedDatabase", initialDatabase);
    } catch (error) {
      console.error("Error in database validation:", error);
      setSelectedDatabase(DEFAULT_DATABASE);
      sessionStorage.setItem("selectedDatabase", DEFAULT_DATABASE);
    }
  }, [databases]);

  useLayoutEffect(() => {
    try {
      const savedStyle = sessionStorage.getItem("selectedStyle");
      let initialStyle = DEFAULT_STYLE;
      if (savedStyle && styles[savedStyle] && styles[savedStyle].type !== "maintenance") {
        initialStyle = savedStyle;
      }
      setSelectedStyle(initialStyle);
      sessionStorage.setItem("selectedStyle", initialStyle);
    } catch (error) {
      console.error("Error in style validation:", error);
      setSelectedStyle(DEFAULT_STYLE);
      sessionStorage.setItem("selectedStyle", DEFAULT_STYLE);
    }
  }, [styles]);

  const cancelGeneration = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const sendMessage = async (
    chatId: string,
    content: string,
    assistanceMode: string
  ) => {
    if (isGenerating) {
      cancelGeneration();
    }
    abortControllerRef.current = new AbortController();

    const userMessage: Message = {
      id: uuidv4(),
      sender: "user",
      content: content,
      metadata: {},
      timestamp: new Date(),
    };

    const botPlaceholder: Message = {
      id: uuidv4(),
      sender: "bot",
      content: "",
      metadata: {},
      timestamp: new Date(),
    };

    setMessages((prevMessages) => [...prevMessages, userMessage, botPlaceholder]);
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat.id === chatId ? { ...chat, updated: new Date() } : chat
      )
    );
    setIsGenerating(true);

    try {
      const currentModel = selectedModel || sessionStorage.getItem("selectedModel") || "default";
      const currentDatabase = selectedDatabase || sessionStorage.getItem("selectedDatabase") || "all";
      const currentStyle = selectedStyle || sessionStorage.getItem("selectedStyle") || "normal";

      const response = await chatService.sendMessage(
        chatId,
        content,
        assistanceMode,
        currentModel,
        currentDatabase,
        currentStyle,
        abortControllerRef.current.signal
      );

      if (!response.ok || !response.body) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        let boundary;
        while ((boundary = buffer.indexOf('\n')) !== -1) {
            const jsonString = buffer.substring(0, boundary);
            buffer = buffer.substring(boundary + 1);
            if (jsonString.trim() === '') continue;

            try {
                const data = JSON.parse(jsonString) as StreamChunk;

                if (data.type === 'ping') continue;

                setMessages(prev => prev.map(msg => {
                    if (msg.id !== botPlaceholder.id) return msg;

                    if (data.is_final) {
                        return {
                            ...msg,
                            id: data.bot_message_id || msg.id,
                            content: data.content,
                            metadata: data.metadata || {},
                            chart_data: data.chart_data || undefined,
                            model_info: data.model_info ? { ...data.model_info, model: currentModel } : undefined,
                            database_info: data.database_info || undefined,
                            style_info: data.style_info || undefined,
                        };
                    } else {
                        return { ...msg, content: msg.content + (data.content || '') };
                    }
                }));
            } catch (e) {
                console.error("Error parsing chunk:", e, "Chunk:", jsonString);
            }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        console.log("Stream manually cancelled");
        setMessages(prev => prev.map(msg => 
            msg.id === botPlaceholder.id 
            ? {...msg, content: msg.content + "\n\n[Generation cancelled.]", metadata: {...msg.metadata, cancelled: true}} 
            : msg
        ));
      } else {
        const errorMessageContent = "Sorry, an error occurred while processing your message. Please try again.";
        setMessages(prev => prev.map(msg => 
            msg.id === botPlaceholder.id 
            ? {...msg, content: errorMessageContent, metadata: { error: String(err) }} 
            : msg
        ));
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
    }
  };

  const createNewChat = async (
    firstMessage: string,
    assistanceMode: string,
    navigate: (path: string) => void
  ): Promise<string | null> => {
    try {
      const response = await chatService.createNewChat();
      const { chat_id } = response.data;
      if (!chat_id) throw new Error("Chat ID is missing in the response");

      const newChat: Chat = {
        id: chat_id,
        name: "New Chat",
        status: "Generating",
        created: new Date(),
        updated: new Date(),
      };

      setChats((prevChats) => [newChat, ...prevChats]);
      setMessages([]);
      setNewRoomCreated(true);
      setIsInitialized(true);
      navigate(`${initialDatabase ? '/dms' : '/chat'}/${chat_id}`);
      setActiveChat(chat_id);

      await sendMessage(chat_id, firstMessage, assistanceMode);

      try {
        const nameResponse = await chatService.generateChatName(chat_id);
        const { chat_name: updatedName } = nameResponse.data;
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === chat_id ? { ...chat, name: updatedName } : chat
          )
        );
      } catch (err) {
        console.error("Error fetching chat name:", err);
      }
      return chat_id;
    } catch (err) {
      console.error("Error creating new chat:", err);
      return null;
    }
  };

  const cancelChat = async (chatId: string) => {
    try {
      await cancelGeneration();
      await chatService.cancelChat(chatId);
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === chatId ? { ...chat, status: "Cancelled" } : chat
        )
      );
    } catch (err) {
      console.error("Error cancelling chat:", err);
    }
  };

  const editChat = async (chatId: string, new_chat_name: string) => {
    try {
      await chatService.editChat(chatId, new_chat_name.trim());
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === chatId ? { ...chat, name: new_chat_name.trim() } : chat
        )
      );
    } catch (err) {
      console.error("Error updating chat name:", err);
    }
  };

  const feedbackChat = async (chatId: string, rating: number, comment: string) => {
    try {
      await chatService.feedbackChat(chatId, rating, comment);
    } catch (err) {
      console.error("Error submitting feedback:", err);
    }
  };

  const deleteChat = async (chatId: string) => {
    try {
      await chatService.deleteChat(chatId);
      setChats((prevChats) => prevChats.filter((chat) => chat.id !== chatId));
      if (activeChat === chatId) {
        setActiveChat(null);
        navigate(initialDatabase ? "/dms/new" : "/chat/new");
      }
    } catch (err) {
      console.error("Error deleting chat:", err);
    }
  };

  const suggestionPrompt = async () => {
    try {
      const response = await chatService.suggestionPrompt();
      return response.data;
    } catch (err) {
      console.error("Error fetching suggestions:", err);
    }
  };

  return {
    activeChat,
    setActiveChat,
    chats,
    setChats,
    messages,
    setMessages,
    isGenerating,
    setIsGenerating,
    isLoading,
    setIsLoading,
    isLoadingMessages,
    isInitialized,
    setIsInitialized,
    models,
    selectedModel,
    handleSelectModel,
    isLoadingModel,
    databases,
    selectedDatabase,
    handleSelectDatabase,
    isLoadingDatabase,
    styles,
    selectedStyle,
    handleSelectStyle,
    isLoadingStyle,
    hasMoreChats,
    hasMoreMessages,
    handleLoadMoreChats,
    handleLoadMoreMessages,
    fetchChats,
    sendMessage,
    createNewChat,
    cancelChat,
    cancelGeneration,
    editChat,
    feedbackChat,
    deleteChat,
    suggestionPrompt,
  };
};